import React from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, Activity, Moon, Footprints, Clock, Zap, TrendingUp, 
  Calendar, Timer, Target, Award, ArrowLeft
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, XAxis, YAxis, ResponsiveContainer, 
  Tooltip, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';
import { useDisciplineStore } from '../../contexts/disciplineStore';

const HealthStatsComponent = ({ isDarkMode = true, onBackToOverview }) => {
  // Get data from Digital Discipline store
  const { goals, timers, xp, streak } = useDisciplineStore();
  
  // Calculate health-related metrics from Digital Discipline data
  const disciplineHealthMetrics = React.useMemo(() => {
    const healthGoals = goals.filter(g => g.category === 'health');
    const completedHealthGoals = healthGoals.filter(g => g.completed);
    const healthTimers = timers.filter(t => 
      t.goalId && goals.find(g => g.id === t.goalId && g.category === 'health')
    );
    const completedHealthTimers = healthTimers.filter(t => t.isCompleted);
    
    return {
      healthGoalsTotal: healthGoals.length,
      healthGoalsCompleted: completedHealthGoals.length,
      healthActiveMinutes: completedHealthTimers.reduce((total, timer) => {
        return total + Math.round((timer.duration || 25) / 60);
      }, 0),
      healthStreak: streak, // Use overall streak for health motivation
      healthXP: Math.round(xp * 0.3) // Estimate 30% of XP from health activities
    };
  }, [goals, timers, streak, xp]);

  // Mock health data enhanced with real discipline data
  const healthData = {
    sleepAvg: 7.2,
    steps: 8420,
    activeMinutes: Math.max(45, disciplineHealthMetrics.healthActiveMinutes), // Use real data if higher
    heartRate: 72,
    sleepGoal: 8,
    stepsGoal: 10000,
    activeGoal: 60,
    calories: 2150,
    water: 6.5, // glasses
    weight: 75.2, // kg
    bmi: 22.1,
    healthGoals: disciplineHealthMetrics.healthGoalsTotal,
    completedHealthGoals: disciplineHealthMetrics.healthGoalsCompleted
  };

  const weeklySteps = [
    { day: 'Pzt', steps: 8420, calories: 320 },
    { day: 'Sal', steps: 9200, calories: 380 },
    { day: 'Çar', steps: 7800, calories: 290 },
    { day: 'Per', steps: 10500, calories: 420 },
    { day: 'Cum', steps: 9800, calories: 390 },
    { day: 'Cmt', steps: 12000, calories: 480 },
    { day: 'Paz', steps: 6500, calories: 250 }
  ];

  const sleepData = [
    { day: 'Pzt', hours: 7.2, quality: 82 },
    { day: 'Sal', hours: 6.8, quality: 75 },
    { day: 'Çar', hours: 7.5, quality: 88 },
    { day: 'Per', hours: 6.5, quality: 70 },
    { day: 'Cum', hours: 8.0, quality: 92 },
    { day: 'Cmt', hours: 8.5, quality: 95 },
    { day: 'Paz', hours: 7.8, quality: 85 }
  ];
  const healthMetrics = [
    { 
      name: 'Kalp Sağlığı', 
      value: Math.min(95, 70 + Math.round(disciplineHealthMetrics.healthActiveMinutes / 2)), 
      color: '#ef4444' 
    },
    { 
      name: 'Uyku Kalitesi', 
      value: 78, 
      color: '#3b82f6' 
    },
    { 
      name: 'Aktivite', 
      value: disciplineHealthMetrics.healthGoalsTotal > 0 ? 
             Math.round((disciplineHealthMetrics.healthGoalsCompleted / disciplineHealthMetrics.healthGoalsTotal) * 100) : 
             60, 
      color: '#10b981' 
    },
    { 
      name: 'Beslenme', 
      value: 76, 
      color: '#f59e0b' 
    },
    { 
      name: 'Disiplin Skoru', 
      value: Math.min(100, 50 + disciplineHealthMetrics.healthStreak * 5), 
      color: '#8b5cf6' 
    }
  ];

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 300, damping: 24 }
    }
  };

  const StatCard = ({ title, value, unit, goal, icon, color, progress }) => (
    <motion.div 
      className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
      variants={itemVariants}
      whileHover={{ y: -2 }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <div className={`p-2 rounded-lg`} style={{ backgroundColor: color + '20' }}>
            {React.cloneElement(icon, { size: 20, color })}
          </div>
          <h3 className="ml-3 font-medium text-sm">{title}</h3>
        </div>
      </div>
      
      <div className="mb-2">
        <span className="text-2xl font-bold">{value}</span>
        <span className={`text-sm ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {unit}
        </span>
        {goal && (
          <span className={`text-xs ml-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            / {goal} {unit}
          </span>
        )}
      </div>
      
      {progress && (
        <div className="w-full bg-gray-600 rounded-full h-2">
          <motion.div 
            className="h-2 rounded-full"
            style={{ backgroundColor: color }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
        </div>
      )}
    </motion.div>
  );
  return (
    <div className="space-y-6">
      {/* Back Button */}
      {onBackToOverview && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <button
            onClick={onBackToOverview}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
            }`}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>İstatistiklere Geri Dön</span>
          </button>
        </motion.div>
      )}
      
      {/* Health Overview Cards */}
      <motion.div 
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
        initial="hidden"
        animate="visible"
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }}
      >
        <StatCard 
          title="Uyku"
          value={healthData.sleepAvg}
          unit="saat"
          goal={healthData.sleepGoal}
          icon={<Moon />}
          color="#3b82f6"
          progress={(healthData.sleepAvg / healthData.sleepGoal) * 100}
        />
        
        <StatCard 
          title="Adımlar"
          value={healthData.steps.toLocaleString()}
          unit=""
          goal={healthData.stepsGoal.toLocaleString()}
          icon={<Footprints />}
          color="#10b981"
          progress={(healthData.steps / healthData.stepsGoal) * 100}
        />
        
        <StatCard 
          title="Aktif Dakika"
          value={healthData.activeMinutes}
          unit="dk"
          goal={healthData.activeGoal}
          icon={<Activity />}
          color="#f59e0b"
          progress={(healthData.activeMinutes / healthData.activeGoal) * 100}
        />
        
        <StatCard 
          title="Kalp Atışı"
          value={healthData.heartRate}
          unit="bpm"
          icon={<Heart />}
          color="#ef4444"
        />      </motion.div>

      {/* Health Goals from Digital Discipline */}
      <motion.div 
        className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <Target className="h-5 w-5 mr-2 text-blue-500" />
          Sağlık Hedefleri
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Toplam Hedef</span>
              <Target className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold">{healthData.healthGoals}</p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Sağlık kategorisinde
            </p>
          </div>
          
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Tamamlanan</span>
              <Award className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold">{healthData.completedHealthGoals}</p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Başarıyla tamamlandı
            </p>
          </div>
          
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Başarı Oranı</span>
              <TrendingUp className="h-4 w-4 text-purple-500" />
            </div>
            <p className="text-2xl font-bold">
              {healthData.healthGoals > 0 ? 
                Math.round((healthData.completedHealthGoals / healthData.healthGoals) * 100) : 0}%
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Genel performans
            </p>
          </div>
        </div>
      </motion.div>

      {/* Weekly Activity Chart */}
      <motion.div 
        className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <Footprints className="h-5 w-5 mr-2 text-green-500" />
          Haftalık Aktivite
        </h3>
        
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={weeklySteps}>
              <XAxis dataKey="day" tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }} />
              <YAxis tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                  color: isDarkMode ? '#f3f4f6' : '#1f2937'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="steps" 
                stroke="#10b981" 
                fill="#10b981" 
                fillOpacity={0.3}
                name="Adımlar"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Sleep Quality Chart */}
      <motion.div 
        className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <Moon className="h-5 w-5 mr-2 text-blue-500" />
          Uyku Kalitesi
        </h3>
        
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sleepData}>
              <XAxis dataKey="day" tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }} />
              <YAxis tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                  color: isDarkMode ? '#f3f4f6' : '#1f2937'
                }}
              />
              <Bar dataKey="hours" fill="#3b82f6" name="Uyku Saati" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Health Metrics Overview */}
      <motion.div 
        className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <Target className="h-5 w-5 mr-2 text-purple-500" />
          Sağlık Metrikleri
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={healthMetrics}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {healthMetrics.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="space-y-4">
            {healthMetrics.map((metric, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-3"
                    style={{ backgroundColor: metric.color }}
                  />
                  <span className="text-sm">{metric.name}</span>
                </div>
                <span className="font-medium">{metric.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Additional Health Info */}
      <motion.div 
        className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <Award className="h-5 w-5 mr-2 text-amber-500" />
          Ek Sağlık Bilgileri
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <p className={`text-xs opacity-70 mb-1`}>Kalori</p>
            <p className="text-lg font-medium">{healthData.calories}</p>
          </div>
          
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <p className={`text-xs opacity-70 mb-1`}>Su (bardak)</p>
            <p className="text-lg font-medium">{healthData.water}</p>
          </div>
          
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <p className={`text-xs opacity-70 mb-1`}>Kilo (kg)</p>
            <p className="text-lg font-medium">{healthData.weight}</p>
          </div>
          
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <p className={`text-xs opacity-70 mb-1`}>BMI</p>
            <p className="text-lg font-medium">{healthData.bmi}</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default HealthStatsComponent;
